import { z } from 'zod';
import { AuditSchema, IdSchema } from './common.ts';
import { MoneySchema } from '../money.ts';
import { CostTypeSchema, MAX_MARKUP_MILLIONTHS } from './cost-code.ts';

/**
 * Line items a company uses again and again.
 *
 * The point is typing. A builder who prices "2x6 stud, 92 5/8" on forty
 * estimates a year should type it once, and should not have to remember
 * which cost code it belongs under or what markup they usually put on it.
 * So a catalog item is **a line waiting to be used**, not a product record:
 * it carries exactly the fields an estimate line needs and nothing a
 * stock-keeping system would want.
 *
 * Two things are deliberately absent, and one used to be:
 *
 * - **No stock level**, still — but no longer because the question is a
 *   different system's. It is answered by `stockMovements` and
 *   `inventoryLevels` beside this collection (INV-01), and the reasoning
 *   below is exactly *why* it is answered there rather than by a field
 *   here: a catalog item is a line waiting to be used, and stock is a
 *   fact about this afternoon. `docs/features/22-inventory.md` §1.
 * - **No price history.** `unitCost` is what the item costs *now*; what a
 *   job paid is on the job's own line, frozen there when the line was
 *   written. A catalog that rewrote history when a price changed would make
 *   last year's estimates disagree with last year's bills.
 * - **No per-job override here.** This one has *moved* rather than held.
 *   An override now exists in two places — `unitPriceOverride` on this
 *   item, which is the quoted rate every future line starts from, and
 *   `unitPriceOverride` on a `CostLine`, which is one job only
 *   (INV-00c, INV-05b). What has not changed is the direction: the
 *   item's value is **copied onto** a line and a line's value **never
 *   writes back**, so changing an item changes what the next line
 *   copies and nothing already written. That was always the rule this
 *   bullet was really about, and saying "no override" instead of
 *   saying the rule is what let the sentence go stale.
 */
export const CatalogItemSchema = AuditSchema.extend({
  id: IdSchema,
  companyId: IdSchema,
  title: z.string().trim().min(1).max(200),
  description: z.string().trim().max(2_000).default(''),
  /**
   * The code this item costs against.
   *
   * Required, unlike on a change order line. A catalog exists to stop
   * people guessing, and an item with no code would put the guess back.
   */
  costCodeId: IdSchema,
  costType: CostTypeSchema.optional(),
  unitCost: MoneySchema,
  /** `each`, `sq ft`, `hour`. Free text, because every trade has its own. */
  unit: z.string().trim().max(20).optional(),
  /**
   * Markup in the same units as `CoLine.markupMillionths` —
   * ten-thousandths of a percent, so 20% is `200_000`. Use
   * `markupFromPercent`. The field name is wrong by a hundred and the
   * rename is P5-00.
   *
   * Optional, and the difference from zero matters: absent means "use the
   * cost code's default", zero means "this item is passed through at
   * cost". A pass-through item with no markup must not silently acquire
   * the code's 20%.
   */
  markupMillionths: z.number().int().min(0).max(MAX_MARKUP_MILLIONTHS).optional(),
  /**
   * Who it is usually bought from (the **first** vendor, historically).
   *
   * Superseded by `vendors` below and kept because every item written
   * before 2026-10-29 carries it. Nothing reads these two directly:
   * `itemVendors` is the one place that knows about both shapes, so a
   * reader cannot accidentally see only half an item's suppliers.
   */
  vendorContactId: IdSchema.optional(),
  /**
   * Their part number, so somebody on the telephone can quote it.
   *
   * Theirs, not ours: `sku` below is this company's own. Two numbers for
   * one thing looks redundant until a vendor changes a part code and the
   * builder's stock records have to survive it.
   */
  vendorItemNumber: z.string().trim().max(80).optional(),
  /**
   * Everyone this part can be bought from (INV2-15, SCRUM-284).
   *
   * The maintainer's words: *"a sku in our system can receive qty from
   * multiple vendors at different costs and varying item numbers."* One
   * of our stock numbers, several suppliers, each with their own part
   * code.
   *
   * **Keyed by vendor**, decided 2026-10-29: a vendor appears at most
   * once on an item, and giving them a new part number replaces the one
   * that was there. The trade accepted with that is a superseded code
   * has nowhere to live.
   *
   * The rules this has to keep, from the same note:
   *
   * - one vendor cannot have the same part number twice on one item —
   *   which the key makes impossible rather than refused;
   * - one vendor cannot use one part number for **two different SKUs**,
   *   because a scanned number would then be ambiguous. That one spans
   *   documents and is enforced in `catalog.ts` on the way in;
   * - two different vendors **may** share a part number for the same
   *   SKU, and may equally use different ones.
   *
   * No price here. Receiving stock already moves the item's `unitCost`
   * to the latest price paid, and what each vendor last charged is a
   * smaller question than the quoted price list §9 of the feature doc
   * rules out — worth separating from this.
   */
  /*
    Optional rather than defaulted to `[]`, like `contacts.system`: an
    item written before this existed is correct as it stands, and
    nothing has to be migrated to say "no vendors" where the two older
    fields already say who. `itemVendors` reads both shapes.
  */
  vendors: z
    .array(
      z.object({
        vendorContactId: IdSchema,
        vendorItemNumber: z.string().trim().max(80).optional(),
      }),
    )
    .max(50)
    .optional(),
  /**
   * This company's own stock number, `SKU-0001`.
   *
   * **Allocated by a callable, never written by a client**, because it
   * is a sequence read out loud — the same reason a purchase order
   * number is. Optional because the thousands of catalog items that
   * exist before this feature have none and do not need one: a SKU is
   * for the things you keep on a shelf.
   */
  sku: z.string().trim().max(32).optional(),
  /**
   * Whether this is a thing the company holds stock of.
   *
   * A catalog is mostly lines waiting to be used — a labour rate, a
   * subcontract price — and none of those have a shelf. Marking the ones
   * that do keeps the stock screens about stock rather than about every
   * line the company has ever priced.
   */
  stocked: z.boolean().default(false),
  /**
   * Where this part lives when nobody has taken it anywhere (INV2-06,
   * SCRUM-268).
   *
   * A **home** location, which is the half of the maintainer's request
   * that answers "where do I go to find this". It is not a quantity per
   * location: the same part being 300 in the warehouse and 100 on a job
   * is a different model for `inventoryLevels` and is deliberately not
   * built yet — see `stock-location.ts` and the feature doc §8b.
   *
   * `rack` and `bin` are free text on purpose. They are written on the
   * shelf in somebody's handwriting, and a company that numbers its
   * racks `A-12` is not doing anything wrong.
   */
  locationId: IdSchema.optional(),
  rack: z.string().trim().max(20).optional(),
  bin: z.string().trim().max(20).optional(),
  /**
   * What the customer pays per unit, instead of cost plus markup.
   *
   * The quoted rate: connectors are $2.50 whatever we paid. Copied onto
   * a cost line when the item is used, and the line's copy is what the
   * customer is billed — **editing it on the job never writes back
   * here**, which is this entity's existing rule in the direction it has
   * not had to state before.
   *
   * Absent is not zero. `undefined` means "use cost plus markup";
   * `{ amount: 0 }` means somebody is giving it away.
   */
  unitPriceOverride: MoneySchema.optional(),
  /** A photograph, for the things a description cannot settle. */
  imageFileId: IdSchema.optional(),
  /** Retired rather than deleted, for the reason cost codes are. */
  active: z.boolean().default(true),
  tagIds: z.array(IdSchema).default([]),
});
export type CatalogItem = z.infer<typeof CatalogItemSchema>;

/**
 * A bundle of items with quantities: "bathroom rough-in", "window opening".
 *
 * Stored as references with quantities rather than as copies of the items,
 * so correcting an item's price corrects every assembly that uses it. The
 * alternative — freezing the items into the assembly — means a price
 * change has to be chased through every bundle, which is exactly the
 * typing this feature exists to avoid.
 *
 * Adding an assembly to an estimate copies its items out as **separate
 * lines**, not as one line. A builder needs to see the studs and the
 * sheathing separately to cost them, and an assembly that collapsed into a
 * single line would hide which part got expensive.
 */
export const AssemblyLineSchema = z.object({
  catalogItemId: IdSchema,
  /** Thousandths, so 2.5 is exact — the same convention as every quantity. */
  quantityThousandths: z.number().int().positive(),
});
export type AssemblyLine = z.infer<typeof AssemblyLineSchema>;

export const CatalogAssemblySchema = AuditSchema.extend({
  id: IdSchema,
  companyId: IdSchema,
  title: z.string().trim().min(1).max(200),
  description: z.string().trim().max(2_000).default(''),
  lines: z.array(AssemblyLineSchema).default([]),
  active: z.boolean().default(true),
  tagIds: z.array(IdSchema).default([]),
});
export type CatalogAssembly = z.infer<typeof CatalogAssemblySchema>;

/**
 * A company's own stock number: `SKU-0001`.
 *
 * **Zero-padded to four**, for the reason a purchase order number is:
 * sorted as text, `SKU-0002` must come before `SKU-0010`, and an
 * unpadded sequence sorts `10` before `2`. A spreadsheet is where this
 * list ends up and a spreadsheet sorts text.
 *
 * No company prefix, unlike a purchase order. A PO number is read out to
 * a supplier who works for several builders and needs to know whose job
 * it is; a SKU is read off a shelf inside one company, and a prefix
 * would be four characters of noise on every label.
 */
export function formatSku(sequence: number): string {
  return `SKU-${String(sequence).padStart(4, '0')}`;
}

/** The sequence out of a SKU, or `undefined` if it is not one. */
export function parseSku(raw: string): number | undefined {
  const match = /^SKU-(\d+)$/.exec(raw.trim().toUpperCase());
  if (!match) return undefined;
  const value = Number(match[1]);
  return Number.isInteger(value) && value > 0 ? value : undefined;
}

/**
 * Two catalog titles are the same title once case and spacing are set aside.
 *
 * A warning rather than a refusal, unlike a cost code number: two items
 * called "Stud" at different lengths is a real thing a company might have,
 * and refusing the second would be the tool arguing with its user. The
 * settings page says "you already have one called that" and lets them
 * decide.
 */
export function catalogTitleKey(title: string): string {
  return title.trim().toLowerCase().replace(/\s+/g, ' ');
}

/**
 * The markup to put on a line made from this item.
 *
 * The three-way distinction is the whole reason this is a function:
 *
 * - the item says `200_000` → use it;
 * - the item says `0` → **zero**, because somebody chose to pass this
 *   through at cost;
 * - the item says nothing → fall back to the cost code's default.
 *
 * Writing `item.markupMillionths ?? code.defaultMarkupMillionths` gets
 * the first and third right and the second wrong, which is the bug this
 * function exists to prevent: a pass-through item silently acquiring the
 * code's markup and appearing on an owner's invoice with 20% on it.
 */
export function markupForLine(
  item: Pick<CatalogItem, 'markupMillionths'>,
  code: { readonly defaultMarkupMillionths: number } | null,
): number {
  if (item.markupMillionths !== undefined) return item.markupMillionths;
  return code?.defaultMarkupMillionths ?? 0;
}

/** Catalog items by id, for rendering an assembly's lines. */
export function catalogItemsByIdFrom(
  items: readonly CatalogItem[],
): ReadonlyMap<string, CatalogItem> {
  return new Map(items.map((item) => [item.id, item] as const));
}

export interface AssemblyLineView {
  readonly catalogItemId: string;
  readonly quantityThousandths: number;
  /** Null when the item has been retired or removed from the catalog. */
  readonly item: CatalogItem | null;
}

/**
 * An assembly's lines with their items resolved.
 *
 * A line whose item is gone is returned with `item: null` rather than
 * dropped, for the reason orphaned cost codes are shown: an assembly that
 * quietly lost a line is an assembly that prices a job short, and nobody
 * would know to look. The screen shows it as missing so somebody can fix
 * the bundle.
 */
export function resolveAssembly(
  assembly: Pick<CatalogAssembly, 'lines'>,
  items: readonly CatalogItem[],
): readonly AssemblyLineView[] {
  const byId = catalogItemsByIdFrom(items);
  return assembly.lines.map((line) => ({
    catalogItemId: line.catalogItemId,
    quantityThousandths: line.quantityThousandths,
    item: byId.get(line.catalogItemId) ?? null,
  }));
}

/** Whether every item in this assembly still exists. */
export function assemblyIsComplete(
  assembly: Pick<CatalogAssembly, 'lines'>,
  items: readonly CatalogItem[],
): boolean {
  const byId = catalogItemsByIdFrom(items);
  return assembly.lines.every((line) => byId.has(line.catalogItemId));
}
